import {
  Box,
  useTheme,
  Typography,
  FormHelperText,
  Grid,
  Dialog,
  DialogContent,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import TreffasButton from "src/components/TreffasButton";
import { useNavigate } from "react-router-dom";
import IsMounted from "src/components/IsMounted";
import http from "src/utils/http";
import TreffasStyle from "src/utils/TreffasStyle";
import PageNotFound from "src/components/PageNotFound";
import { useTranslation } from "react-i18next";
import SL from "src/assets/images/survey-thank.png";
import TreffasTextField from "src/components/TreffasTextField";
import LoadingScreen from "src/components/LoadingScreen";
import { useSnackbarContext } from "src/context/SnackbarContext";
import QuestionEssayAnswer from "./components/QuestionEssayAnswer";
import QuestionShortAnswer from "./components/QuestionShortAnswer";
import QuestionNPS from "./components/QuestionNPS";

const SurveyFormBeforeBooking = ({
  surveyId,
  surveySlug,
  setValue,
  setSurveyHasAnswered,
  surveyIsRequired,
  onAddAnswer,
  customStyle = {},
}) => {
  const [isLoading, setIsLoading] = useState(true);

  const [firstname, setFirstname] = useState("");
  const [lastname, setLastname] = useState("");
  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState("");
  const [requiredErrors, setRequiredErrors] = useState({});

  const [details, setDetails] = useState(null);
  const mounted = IsMounted();

  const themes = useTheme();
  const [randomState, setRandomState] = useState({});
  const [randomErrors, setRandomErrors] = useState({});
  const [npsAnswers, setNpsAnswers] = useState({});
  const [thankyDialog, setThankyouDialog] = useState(false);

  const navigate = useNavigate();

  const [isProcess, setIsProcess] = useState(false);
  const { showSnackbar } = useSnackbarContext();
  const { t } = useTranslation();

  const getSurveyDetails = React.useCallback(() => {
    http
      .get("/api/general/survey/details", {
        params: {
          survey_id: surveyId,
          slug: surveySlug,
        },
      })
      .then((response) => {
        if (mounted.current) {
          setDetails(response.data.data);
        }
      })
      .catch((err) => console.log("error:", err.message))
      .finally(() => setIsLoading(false));
  }, [mounted, surveyId, surveySlug]);

  const handleMultipleAnswerWithLimit = (xx, data, key) => {
    let tricks = xx.target.checked ? "p" : "m";
    let curCount = randomState?.[`${data.id}`]?.selectedCount ?? 0;
    let sellimits = data.limits;

    setRandomState({
      ...randomState,
      [data.id]: {
        selectedCount: tricks === "p" ? curCount + 1 : curCount - 1,
      },
    });

    if (xx.target.checked && sellimits > 0 && curCount > 2) {
      setRandomErrors({
        ...randomErrors,
        [data.id]: {
          hasError: true,
          message: t("survey.take.form.error.selected_limits", { sellimits }),
        },
      });
    } else {
      setRandomErrors({
        ...randomErrors,
        [data.id]: {
          hasError: false,
          message: ``,
        },
      });
    }
  };

  const validateFields = (error) => {
    if (firstname.trim() === "" || firstname.length === 0) {
      error = "firstname-required";
      setRequiredErrors((requiredErrors) => {
        return {
          ...requiredErrors,
          firstname: {
            required: true,
            message: t(
              "individual.booking.schedule.take_survey.required.firstname"
            ),
          },
        };
      });
    }

    if (lastname.trim() === "" || lastname.length === 0) {
      error = "lastname-required";
      setRequiredErrors((requiredErrors) => {
        return {
          ...requiredErrors,
          lastname: {
            required: true,
            message: t(
              "individual.booking.schedule.take_survey.required.lastname"
            ),
          },
        };
      });
    }

    if (email.trim() === "" || email.length === 0) {
      error = "email-required";
      setRequiredErrors((requiredErrors) => {
        return {
          ...requiredErrors,
          email: {
            required: true,
            message: t(
              "individual.booking.schedule.take_survey.required.email"
            ),
          },
        };
      });
    }

    return error;
  };

  const handleSurvey = (e) => {
    e.preventDefault();
    e.persist();
    let error = [];
    setRequiredErrors({});
    let formdata = new FormData(e.target);
    formdata.append("survey_id", surveyId);
    formdata.append("firstname", firstname);
    formdata.append("lastname", lastname);
    formdata.append("email", email);
    formdata.append("mobile", phone);
    formdata.append("survey_id", surveyId);
    formdata.append("baseUrl", window.location.origin);

    // Validate required NPS questions
    if (details?.questionslist) {
      details.questionslist.forEach((question) => {
        if (
          question.type === "question-nps" &&
          Boolean(parseInt(question.is_required)) &&
          !npsAnswers[question.id]
        ) {
          error = "nps-required";
          setRandomErrors((prev) => ({
            ...prev,
            [question.id]: {
              hasError: true,
              message: t("survey.take.form.error.required"),
            },
          }));
        }
      });
    }

    if (Object.values(randomErrors).filter((x) => x.hasError).length > 0) {
      error = "max selected checkbox";
      showSnackbar(
        t("individual.booking.schedule.take_survey.alert.form_error"),
        "error"
      );
    }

    error = validateFields(error);

    if (error.length > 0) {
      console.log("form has an error:", error);
      return;
    }

    setIsProcess(true);

    http
      .post(`/api/general/survey/save-respondent-answer`, formdata)
      .then((response) => {
        if (response.data.message === "success") {
          setThankyouDialog(true);
          onAddAnswer(surveyId, false, response.data.respondent_id);
          setTimeout(() => {
            handleCloseDialog();
          }, 5000);
        }

        if (response?.data?.has_error === "limit_one_response_error") {
          showSnackbar(t("survey.error.limit_one_response"), "info");
        }
      })
      .catch((err) => console.log(err.message))
      .finally(() => setIsProcess(false));
  };

  const handleCloseDialog = () => {
    [
      { name: "firstname", value: firstname },
      { name: "lastname", value: lastname },
      {
        name: "mobile",
        value: phone,
      },
      { name: "email", value: email },
    ].forEach(({ name, value }) => setValue(name, value));
    setSurveyHasAnswered(true);
    setThankyouDialog(false);
  };

  const handleOnSurveySkip = () => {
    setSurveyHasAnswered(true);
    onAddAnswer(surveyId, true, null);
  };

  useEffect(() => {
    getSurveyDetails();
  }, [getSurveyDetails]);

  if (isLoading) {
    return <LoadingScreen />;
  }

  if (details === null) {
    return <PageNotFound />;
  }

  return (
    <Box
      sx={{
        mt: "58px",
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: "10px",
          mb: "40px",
        }}
      >
        <Box>
          <Typography
            sx={{
              fontFamily: "var(--font-grenettepromedium)",
              fontSize: "18px",
              lineHeight: "23px",
              alignItems: "center",
              color: "#060A2D",
            }}
          >
            {t("individual.booking.schedule.survey")}
          </Typography>

          <Typography
            sx={{
              fontWeight: 400,
              fontSize: "14px",
              lineHeight: "18px",
              color: "#878787",
            }}
          >
            {t("individual.booking.schedule.survey.take_to_continue")}
          </Typography>
        </Box>
      </Box>
      <Box>
        <Box>
          <Typography
            sx={{
              fontWeight: 500,
              fontSize: "14px",
              lineHeight: "31px",
              textTransform: "capitalize",
            }}
          >
            {details.name}
          </Typography>

          {details?.description && (
            <Typography
              sx={{
                fontWeight: 400,
                fontSize: "14px",
                lineHeight: "18px",
                color: "#878787",
              }}
            >
              {details?.description}
            </Typography>
          )}
        </Box>

        <form onSubmit={handleSurvey}>
          <Box>
            {details?.questionslist.length > 0 ? (
              <>
                {details?.questionslist.map((item, key) => {
                  return (
                    <Box
                      key={key}
                      sx={{
                        mt: "23px",
                        mb: 3,
                        border: "1px solid #D9DBE9",
                        borderRadius: "8px",
                        padding: "25px 28px",
                        backgroundColor: "#FFFFFF",
                      }}
                    >
                      <Box>
                        <Typography
                          sx={{
                            fontWeight: 400,
                            fontSize: "14px",
                            lineHeight: "24px",
                          }}
                        >
                          {key + 1}. {item.question}{" "}
                          {Boolean(parseInt(item.is_required)) && (
                            <span style={{ color: "red" }}> * </span>
                          )}
                        </Typography>
                      </Box>

                      {/* error display here */}
                      <Box>
                        {Boolean(randomErrors?.[`${item.id}`]?.hasError) && (
                          <FormHelperText error>
                            {randomErrors?.[`${item.id}`]?.message}{" "}
                          </FormHelperText>
                        )}
                      </Box>

                      {/* question-short-answer */}
                      {item.type === "question-short-answer" && (
                        <Box mt={1}>
                          <QuestionShortAnswer item={item} themes={themes} />
                        </Box>
                      )}

                      {/* question-short-answer */}
                      {item.type === "question-comment-essay" && (
                        <Box mt={1}>
                          <QuestionEssayAnswer item={item} themes={themes} />
                        </Box>
                      )}

                      {/* question-one-answer */}
                      {item.type === "question-one-answer" && (
                        <Box mx={3} mt={1}>
                          <Grid container spacing={"10px"}>
                            {item.choices.length > 0 &&
                              item.choices.map((ch, chKey) => (
                                <Grid item xs={12} md={12} key={chKey}>
                                  <Box
                                    sx={{
                                      display: "flex",
                                      alignItems: "center",
                                    }}
                                  >
                                    <input
                                      name={`answer[${item.id}][]`}
                                      value={ch.id}
                                      type="radio"
                                      id={`flexRadioDefault1dfdsf${ch.id}`}
                                      {...(parseInt(chKey) + 1 === 1 &&
                                        Boolean(parseInt(item.is_required)) && {
                                        required: "required",
                                      })}
                                      style={{
                                        width: "20px",
                                        height: "20px",
                                        accentColor: customStyle?.accentColor
                                          ? customStyle?.accentColor
                                          : "var(--color-black)",
                                      }}
                                      className="custom-radio"
                                    />
                                    <label
                                      htmlFor={`flexRadioDefault1dfdsf${ch.id}`}
                                      style={styles.label(customStyle)}
                                    >
                                      {ch.options}
                                    </label>
                                  </Box>
                                </Grid>
                              ))}
                          </Grid>
                        </Box>
                      )}

                      {/* question-multiple-answer */}
                      {item.type === "question-multiple-answer" && (
                        <Box mx={3} mt={1}>
                          <Grid container spacing={"10px"}>
                            {item.choices.length > 0 &&
                              item.choices.map((ch, chKey) => (
                                <Grid item xs={12} md={12} key={chKey}>
                                  <Box
                                    sx={{
                                      display: "flex",
                                      alignItems: "center",
                                    }}
                                  >
                                    <input
                                      name={`answer[${item.id}][]`}
                                      value={ch.id}
                                      type="checkbox"
                                      id={`flexCheckDefault45345${ch.id}`}
                                      {...(Boolean(item.is_required) && {
                                        required: "required",
                                      })}
                                      style={{
                                        width: "20px",
                                        height: "20px",
                                        accentColor:
                                          customStyle?.accentColor ??
                                          "var(--color-black)",
                                      }}
                                      className="custom-radio"
                                      onChange={(xx) => {
                                        // remove required //
                                        if (Boolean(item.is_required)) {
                                          if (xx.target.checked) {
                                            let selector =
                                              document.getElementsByName(
                                                `answer[${item.id}][]`
                                              );
                                            selector.forEach((e) =>
                                              e.removeAttribute("required")
                                            );
                                          } else {
                                            let selector =
                                              document.getElementsByName(
                                                `answer[${item.id}][]`
                                              );
                                            let hasselected = [];
                                            selector.forEach((e) => {
                                              if (e.checked) {
                                                hasselected.push(e.value);
                                              }
                                            });

                                            if (hasselected.length === 0) {
                                              selector.forEach((e) =>
                                                e.setAttribute(
                                                  "required",
                                                  "required"
                                                )
                                              );
                                            }
                                          }
                                        }

                                        // check for limit //
                                        handleMultipleAnswerWithLimit(
                                          xx,
                                          item,
                                          key
                                        );
                                      }}
                                    />
                                    <label
                                      htmlFor={`flexCheckDefault45345${ch.id}`}
                                      style={styles.label(customStyle)}
                                    >
                                      {ch.options}
                                    </label>
                                  </Box>
                                </Grid>
                              ))}
                          </Grid>
                        </Box>
                      )}

                      {/* question-true-false */}
                      {item.type === "question-true-false" && (
                        <Box mx={3} mt={1}>
                          <Grid container spacing={"10px"}>
                            {item.choices.length > 0 &&
                              item.choices.map((ch, chKey) => (
                                <Grid item xs={12} md={12} key={chKey}>
                                  <Box
                                    sx={{
                                      display: "flex",
                                      alignItems: "center",
                                    }}
                                  >
                                    <input
                                      name={`answer[${item.id}][]`}
                                      value={ch.id}
                                      type="radio"
                                      id={`flexRadioDefault1dfdsf7788${ch.id}`}
                                      {...(parseInt(chKey) + 1 === 1 &&
                                        Boolean(parseInt(item.is_required)) && {
                                        required: "required",
                                      })}
                                      style={{
                                        width: "20px",
                                        height: "20px",
                                        accentColor: customStyle?.accentColor
                                          ? customStyle?.accentColor
                                          : "var(--color-black)",
                                      }}
                                      className="custom-radio"
                                    />
                                    <label
                                      htmlFor={`flexRadioDefault1dfdsf7788${ch.id}`}
                                      style={styles.label(customStyle)}
                                    >
                                      {ch.options}
                                    </label>
                                  </Box>
                                </Grid>
                              ))}
                          </Grid>
                        </Box>
                      )}

                      {/* question-yes-no */}
                      {item.type === "question-yes-no" && (
                        <Box mx={3} mt={1}>
                          <Grid container spacing={"10px"}>
                            {item.choices.length > 0 &&
                              item.choices.map((ch, chKey) => (
                                <Grid item xs={12} md={12} key={chKey}>
                                  <Box
                                    sx={{
                                      display: "flex",
                                      alignItems: "center",
                                    }}
                                  >
                                    <input
                                      name={`answer[${item.id}][]`}
                                      value={ch.id}
                                      type="radio"
                                      id={`flexRadioDefault1dfdsf7788df${ch.id}`}
                                      {...(parseInt(chKey) + 1 === 1 &&
                                        Boolean(parseInt(item.is_required)) && {
                                        required: "required",
                                      })}
                                      style={{
                                        width: "20px",
                                        height: "20px",
                                        accentColor: customStyle?.accentColor
                                          ? customStyle?.accentColor
                                          : "var(--color-black)",
                                      }}
                                      className="custom-radio"
                                    />
                                    <label
                                      htmlFor={`flexRadioDefault1dfdsf7788df${ch.id}`}
                                      style={styles.label(customStyle)}
                                    >
                                      {ch.options}
                                    </label>
                                  </Box>
                                </Grid>
                              ))}
                          </Grid>
                        </Box>
                      )}

                      {/* question-nps */}
                      {item.type === "question-nps" && (
                        <Box>
                          {/* Error display for NPS */}
                          {Boolean(randomErrors?.[`${item.id}`]?.hasError) && (
                            <FormHelperText error sx={{ mt: 1, mb: 1 }}>
                              {randomErrors?.[`${item.id}`]?.message}
                            </FormHelperText>
                          )}

                          <QuestionNPS
                            item={item}
                            customStyle={customStyle}
                            npsAnswers={npsAnswers}
                            setNpsAnswers={setNpsAnswers}
                          />
                        </Box>
                      )}
                    </Box>
                  );
                })}

                <Box>
                  <Grid container spacing="20px">
                    <Grid item xs={12} md={6}>
                      <Box>
                        <Typography sx={TreffasStyle.formLabel}>
                          {t(
                            "individual.booking.schedule.take_survey.firstname"
                          )}
                        </Typography>

                        <Box>
                          <TreffasTextField
                            fullWidth
                            value={firstname}
                            onChange={(e) => setFirstname(e.target.value)}
                            placeholder={t(
                              "individual.booking.schedule.take_survey.placeholder.firstname"
                            )}
                            sx={TreffasStyle.formInputLarge("input")}
                            error={Boolean(requiredErrors?.firstname?.required)}
                            helperText={requiredErrors?.firstname?.message}
                          />
                        </Box>
                      </Box>
                    </Grid>

                    <Grid item xs={12} md={6}>
                      <Box>
                        <Typography sx={TreffasStyle.formLabel}>
                          {t(
                            "individual.booking.schedule.take_survey.lastname"
                          )}
                        </Typography>

                        <Box>
                          <TreffasTextField
                            fullWidth
                            value={lastname}
                            onChange={(e) => setLastname(e.target.value)}
                            placeholder={t(
                              "individual.booking.schedule.take_survey.placeholder.lastname"
                            )}
                            sx={TreffasStyle.formInputLarge("input")}
                            error={Boolean(requiredErrors?.lastname?.required)}
                            helperText={requiredErrors?.lastname?.message}
                          />
                        </Box>
                      </Box>
                    </Grid>

                    <Grid item xs={12} md={6}>
                      <Box>
                        <Typography sx={TreffasStyle.formLabel}>
                          {t("individual.booking.schedule.take_survey.phone")}
                        </Typography>
                        <Box>
                          <TreffasTextField
                            fullWidth
                            value={phone}
                            onChange={(e) => setPhone(e.target.value)}
                            placeholder={t(
                              "individual.booking.schedule.take_survey.placeholder.phone"
                            )}
                            sx={TreffasStyle.formInputLarge("input")}
                          />
                        </Box>
                      </Box>
                    </Grid>

                    <Grid item xs={12} md={6}>
                      <Box>
                        <Typography sx={TreffasStyle.formLabel}>
                          {t("individual.booking.schedule.take_survey.email")}
                        </Typography>

                        <Box>
                          <TreffasTextField
                            fullWidth
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            placeholder={t(
                              "individual.booking.schedule.take_survey.placeholder.email"
                            )}
                            type="email"
                            sx={TreffasStyle.formInputLarge("input")}
                            error={Boolean(requiredErrors?.email?.required)}
                            helperText={requiredErrors?.email?.message}
                          />
                        </Box>
                      </Box>
                    </Grid>
                  </Grid>
                </Box>

                {/* optins options */}
                <Box
                  sx={{
                    mt: "50px",
                    mb: "30px",
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "flex-end",
                      gap: "20px",
                      flexDirection: { xs: "column", sm: "row" },
                    }}
                  >
                    <Box>
                      <TreffasButton
                        variant="secondary"
                        size="lg"
                        sx={{
                          width: { xs: "100%", sm: "120px" },
                          borderRadius: "12px",
                          fontSize: "14px",
                          lineHeight: "31px",
                        }}
                        onClick={() => navigate(-1)}
                      >
                        {t("survey.take.form.button.cancel")}
                      </TreffasButton>
                    </Box>

                    {!surveyIsRequired && (
                      <Box>
                        <TreffasButton
                          variant="secondary"
                          size="lg"
                          sx={{
                            minWidth: { xs: "100%", sm: "100px" },
                            borderRadius: "12px",
                            fontSize: "14px",
                            lineHeight: "31px",
                          }}
                          onClick={() => handleOnSurveySkip()}
                        >
                          {t(
                            "individual.booking.schedule.take_survey.button.skip_survey"
                          )}
                        </TreffasButton>
                      </Box>
                    )}

                    <Box>
                      <TreffasButton
                        variant="primary"
                        size="lg"
                        sx={{
                          width: { xs: "100%", sm: "120px" },
                          borderRadius: "12px",
                          fontSize: "14px",
                          lineHeight: "31px",
                        }}
                        loading={isProcess}
                        disabled={isProcess}
                        type="submit"
                      >
                        {t("survey.take.form.button.submit")}
                      </TreffasButton>
                    </Box>
                  </Box>
                </Box>
              </>
            ) : (
              <Typography sx={{ color: "var(--color-gray-700)" }}>
                {t("survey.take.form.error.noquestion_found")}
              </Typography>
            )}
          </Box>
        </form>
      </Box>
      <Dialog
        open={thankyDialog}
        fullScreen
        onClose={() => console.log("dialog unclosable")}
      >
        <DialogContent
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              maxWidth: "740px",
              height: "350px",
              border: "1px solid #D9DBE9",
              borderRadius: "8px",
              px: { xs: "10px", md: "120px" },
            }}
          >
            <Box>
              <Box textAlign={"center"}>
                <img
                  src={SL}
                  alt={"treffas survey thank you logo"}
                  style={{
                    width: "88.71px",
                    height: "88px",
                  }}
                />
              </Box>

              <Box>
                <Typography
                  sx={{
                    fontFamily: "var(--font-grenettepromedium)",
                    textTransform: "capitalize",
                    fontSize: "22px",
                    lineHeight: "31px",
                    color: "#060A2D",
                    textAlign: "center",
                  }}
                >
                  {t("survey.take.form.alert.thank_you")}
                </Typography>
              </Box>

              <Box mt={"7px"}>
                <Typography
                  sx={{
                    fontSize: "14px",
                    fontWeight: "400",
                    lineHeight: "24px",
                    textAlign: "center",
                  }}
                >
                  {details?.thankyou_message}
                </Typography>
              </Box>

              <Box
                sx={{
                  mt: "50px",
                  textAlign: "center",
                }}
              >
                <TreffasButton
                  variant="secondary"
                  size="lg"
                  sx={(themes) => ({
                    [themes.breakpoints.up("md")]: {
                      borderRadius: "12px",
                      minWidth: "100px",
                      fontSize: "14px",
                    },
                  })}
                  onClick={() => handleCloseDialog()}
                >
                  {t(
                    "individual.booking.schedule.take_survey.button.close_and_continue_to_booking"
                  )}
                </TreffasButton>
              </Box>
            </Box>
          </Box>
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default SurveyFormBeforeBooking;

const styles = {
  label: (customStyle) => ({
    marginTop: "5px",
    marginLeft: "5px",
    cursor: "pointer",
    textTransform: "capitalize",
    fontWeight: 400,
    fontSize: "14px",
    lineHeight: "24px",
    fontFamily: `var(--font-${customStyle?.bodyFont ? customStyle?.bodyFont : "Lato"
      })`,
    color: customStyle?.textColor ? customStyle?.textColor : "#19181B",
  }),
};
